/**
 * Templates Tab Component
 *
 * Displays a grid of email templates for quick selection.
 * Users can browse and select pre-built templates.
 */

import React, { useState } from "react";
import { Icons } from "../../../../../../views/crm-pages/conversation/constant";
import { EmailTemplate, TemplatesTabProps } from "./types";
import { FaCheck, FaEdit, FaEye } from "react-icons/fa";

interface TemplateCardProps {
  template: EmailTemplate;
  isSelected: boolean;
  onSelect: () => void;
  onPreview: () => void;
  onEditVariables?: () => void;
  processedHtml?: string;
}

/**
 * Individual template card
 */
const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  isSelected,
  onSelect,
  onPreview,
  onEditVariables,
  processedHtml,
}) => {
  // Use processed HTML if available (for selected template with filled variables)
  const displayHtml = isSelected && processedHtml ? processedHtml : template.html_body;
  const hasVariables = template.variables && template.variables.length > 0;

  return (
    <div
      className={`relative bg-white dark:bg-gray-800 rounded-lg border-2 overflow-hidden cursor-pointer transition-all duration-200 ${
        isSelected
          ? "border-green-500 shadow-md"
          : "border-slate-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-500/50 hover:shadow-sm"
      }`}
      onClick={onSelect}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 z-10 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
          <FaCheck size={12} className="text-white" />
        </div>
      )}

      {/* Template Preview */}
      <div className="h-36 bg-white dark:bg-gray-800 border-b border-slate-100 dark:border-gray-700 overflow-hidden">
        {displayHtml ? (
          <div className="w-full h-full overflow-hidden relative">
            <iframe
              srcDoc={displayHtml}
              title={template.name}
              className="border-0 pointer-events-none absolute top-0 left-0"
              style={{
                transform: "scale(0.35)",
                transformOrigin: "top left",
                width: "285%",
                height: "285%",
              }}
              sandbox=""
            />
          </div>
        ) : (
          <div className="w-full h-full bg-slate-50 dark:bg-gray-900 p-3 overflow-hidden">
            {/* Fallback shimmer preview */}
            <div className="space-y-2">
              <div className="h-2.5 bg-indigo-100 dark:bg-indigo-500/20 rounded w-3/4"></div>
              <div className="h-2 bg-slate-200 dark:bg-gray-700 rounded w-full"></div>
              <div className="h-2 bg-slate-200 dark:bg-gray-700 rounded w-5/6"></div>
              <div className="h-2 bg-slate-200 dark:bg-gray-700 rounded w-4/6"></div>
              <div className="h-5 bg-indigo-500 rounded w-1/3 mt-3"></div>
            </div>
          </div>
        )}
      </div>

      {/* Template Info */}
      <div className="p-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-gray-100 truncate flex-1">
            {template.name}
          </h4>
          <div className="flex items-center gap-1">
            {/* Edit Variables button - only show for selected template with variables */}
            {isSelected && hasVariables && onEditVariables && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditVariables();
                }}
                className="p-1 text-green-500 hover:text-green-600 transition-colors"
                title="Edit variables"
              >
                <FaEdit size={12} />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPreview();
              }}
              className="p-1 text-slate-400 dark:text-gray-500 hover:text-green-500 transition-colors"
              title="Preview template"
            >
              <FaEye size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Template Preview Modal
 */
interface TemplatePreviewModalProps {
  template: EmailTemplate | null;
  onClose: () => void;
  onSelect: () => void;
  processedHtml?: string;
}

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  template,
  onClose,
  onSelect,
  processedHtml,
}) => {
  if (!template) return null;

  // Use processed HTML if available, otherwise use raw template HTML
  const displayHtml = processedHtml || template.html_body;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[1001] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-gray-800">
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-gray-100">{template.name}</h3>
            <p className="text-xs text-slate-500 dark:text-gray-400">Subject: {template.subject}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 transition-colors"
          >
            <span className="text-xl">&times;</span>
          </button>
        </div>

        {/* Body Preview */}
        <div className="p-6 overflow-y-auto max-h-[50vh] bg-slate-50 dark:bg-gray-800/60">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700 p-6">
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: displayHtml }}
            />
          </div>
        </div>

        {/* Variables Info */}
        {template.variables && template.variables.length > 0 && (
          <div className="px-4 py-2 bg-slate-50 dark:bg-gray-800/60 border-t border-slate-100 dark:border-gray-800">
            <p className="text-xs text-slate-500 dark:text-gray-400">
              <span className="font-medium">Variables:</span>{" "}
              {template.variables.map((v) => `{{${v}}}`).join(", ")}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-slate-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onSelect();
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors"
          >
            Use This Template
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for template cards
 */
const TemplateCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-slate-200 dark:border-gray-700 overflow-hidden animate-pulse">
      <div className="h-32 bg-slate-100 dark:bg-gray-700"></div>
      <div className="p-3">
        <div className="h-4 bg-slate-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-slate-100 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
    </div>
  );
};

/**
 * Templates Tab - Grid of email templates
 */
export const TemplatesTab: React.FC<TemplatesTabProps> = ({
  templates,
  onSelectTemplate,
  onEditVariables,
  selectedTemplateId,
  loading = false,
  templateVariables = {},
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(
    null
  );

  /**
   * Get processed HTML with variables replaced
   */
  const getProcessedHtml = (html: string): string => {
    let processedHtml = html;
    Object.entries(templateVariables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      processedHtml = processedHtml.replace(regex, value || `{{${key}}}`);
    });
    return processedHtml;
  };

  // Filter templates by search query
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.template_key.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch && template.is_active;
  });

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-gray-900">
      {/* Search */}
      <div className="p-4 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800">
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-gray-800 rounded-lg p-2 border border-slate-200 dark:border-gray-700">
          <Icons.Search />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="border-none bg-transparent outline-none text-sm text-slate-600 dark:text-gray-300 w-full"
          />
        </div>
      </div>

      {/* Templates Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, index) => (
              <TemplateCardSkeleton key={index} />
            ))}
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-8">
            <Icons.Email size={24} />
            <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">No templates found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => {
              const isSelected = selectedTemplateId === template.id;
              return (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isSelected={isSelected}
                  onSelect={() => onSelectTemplate(template)}
                  onPreview={() => setPreviewTemplate(template)}
                  onEditVariables={
                    isSelected && onEditVariables
                      ? () => onEditVariables(template)
                      : undefined
                  }
                  processedHtml={
                    isSelected ? getProcessedHtml(template.html_body) : undefined
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onSelect={() => onSelectTemplate(previewTemplate)}
          processedHtml={
            selectedTemplateId === previewTemplate.id
              ? getProcessedHtml(previewTemplate.html_body)
              : undefined
          }
        />
      )}
    </div>
  );
};
